import { useState, useEffect } from 'react';
import { BarChart2, Plus, Trash2, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { webinarAPI } from '@/lib/api';
import { toast } from 'sonner';
import useWebinarStore from '@/stores/webinarStore';
import { motion, AnimatePresence } from 'framer-motion';

export default function LivePolls({ currentUser, isHost }) {
  const { activePoll, socket, webinarId } = useWebinarStore();
  const [polls, setPolls] = useState([]);
  const [view, setView] = useState('list'); // list | create | vote | results
  const [loading, setLoading] = useState(false);

  // Form state
  const [question, setQuestion] = useState('');
  const [options, setOptions] = useState([{ id: '1', text: '' }, { id: '2', text: '' }]);
  const [allowMultiple, setAllowMultiple] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState([]);

  useEffect(() => {
    loadPolls();
  }, [webinarId]);

  useEffect(() => {
    if (activePoll) {
      const hasVoted = activePoll.votes?.some(v => v.user_id === currentUser?.id);
      setView(hasVoted || activePoll.status === 'closed' ? 'results' : 'vote');
    } else {
      setView('list');
      loadPolls();
    }
  }, [activePoll, currentUser?.id]);

  const loadPolls = async () => {
    try {
      const { data } = await webinarAPI.listPolls(webinarId);
      setPolls(data);
    } catch { }
  };

  const handleCreate = async () => {
    const validOptions = options.filter(o => o.text.trim());
    if (!question.trim()) return toast.error('La question est requise');
    if (validOptions.length < 2) return toast.error('Au moins 2 options requises');

    setLoading(true);
    try {
      const { data } = await webinarAPI.createPoll(webinarId, {
        question: question.trim(),
        options: validOptions.map(o => ({ text: o.text.trim() })),
        allow_multiple: allowMultiple,
      });
      setPolls([data, ...polls]);
      setView('list');
      setQuestion('');
      setOptions([{ id: '1', text: '' }, { id: '2', text: '' }]);
    } catch {
      toast.error('Erreur lors de la création');
    } finally {
      setLoading(false);
    }
  };

  const handleStart = (pollId) => {
    socket?.emit('webinar:poll:start', { poll_id: pollId });
  };

  const handleEnd = () => {
    if (activePoll) {
      socket?.emit('webinar:poll:end', { poll_id: activePoll.id });
    }
  };

  const handleVote = () => {
    if (!selectedOptions.length || !activePoll) return;
    socket?.emit('webinar:poll:vote', {
      poll_id: activePoll.id,
      option_ids: selectedOptions
    });
  };

  const toggleOption = (id) => {
    if (allowMultiple) {
      setSelectedOptions(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    } else {
      setSelectedOptions([id]);
    }
  };

  if (view === 'create' && isHost) {
    return (
      <div className="flex flex-col h-full bg-zinc-950">
        <div className="p-3 border-b border-zinc-800 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-white">Nouveau sondage</h3>
          <Button variant="ghost" size="sm" onClick={() => setView('list')} className="h-7 text-xs text-zinc-400 hover:text-white">Annuler</Button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          <div>
            <Label className="text-zinc-300">Question</Label>
            <Input value={question} onChange={(e) => setQuestion(e.target.value)}
              placeholder="Posez votre question..." className="mt-1.5 bg-zinc-900 border-zinc-800" />
          </div>
          <div className="space-y-2">
            <Label className="text-zinc-300">Options</Label>
            {options.map((opt, i) => (
              <div key={opt.id} className="flex items-center gap-2">
                <Input value={opt.text} onChange={(e) => {
                  const newOpts = [...options];
                  newOpts[i].text = e.target.value;
                  setOptions(newOpts);
                }} placeholder={`Option ${i + 1}`} className="bg-zinc-900 border-zinc-800" />
                {options.length > 2 && (
                  <Button variant="ghost" size="icon" onClick={() => setOptions(options.filter(o => o.id !== opt.id))}
                    className="h-9 w-9 text-red-400 hover:text-red-300 hover:bg-red-500/10 shrink-0">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={() => setOptions([...options, { id: Date.now().toString(), text: '' }])}
              className="w-full mt-2 border-dashed border-zinc-700 text-zinc-400 hover:text-white hover:bg-zinc-800">
              <Plus className="w-4 h-4 mr-2" /> Ajouter une option
            </Button>
          </div>
          <div className="flex items-center justify-between pt-2">
            <Label className="text-sm text-zinc-400">Choix multiples</Label>
            <Switch checked={allowMultiple} onCheckedChange={setAllowMultiple} />
          </div>
        </div>
        <div className="p-3 border-t border-zinc-800">
          <Button onClick={handleCreate} disabled={loading} className="w-full bg-cyan-600 hover:bg-cyan-700 text-white">
            Créer le sondage
          </Button>
        </div>
      </div>
    );
  }

  if ((view === 'vote' || view === 'results') && activePoll) {
    return (
      <div className="flex flex-col h-full bg-zinc-950">
        <div className="p-3 border-b border-zinc-800 bg-cyan-500/10">
          <h3 className="text-sm font-semibold text-cyan-400 flex items-center gap-2">
            <BarChart2 className="w-4 h-4" /> Sondage en cours
          </h3>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          <h4 className="text-base font-medium text-white mb-4">{activePoll.question}</h4>
          <div className="space-y-3">
            {activePoll.options.map((opt) => {
              const isSelected = selectedOptions.includes(opt.id);
              const percentage = activePoll.total_votes > 0 ? Math.round((opt.votes / activePoll.total_votes) * 100) : 0;

              if (view === 'results') {
                return (
                  <div key={opt.id} className="relative overflow-hidden rounded-lg bg-zinc-900 border border-zinc-800 p-3">
                    <motion.div initial={{ width: 0 }} animate={{ width: `${percentage}%` }}
                      className="absolute inset-y-0 left-0 bg-cyan-500/20 border-r border-cyan-500/50" />
                    <div className="relative flex justify-between text-sm z-10">
                      <span className="text-zinc-200">{opt.text}</span>
                      <span className="font-medium text-cyan-400">{percentage}% ({opt.votes})</span>
                    </div>
                  </div>
                );
              }

              return (
                <button key={opt.id} onClick={() => toggleOption(opt.id)}
                  className={`w-full flex items-center gap-3 p-3 rounded-lg border text-left transition-all ${
                    isSelected ? 'bg-cyan-500/10 border-cyan-500/50 text-white' : 'bg-zinc-900 border-zinc-800 text-zinc-400 hover:border-zinc-700 hover:text-zinc-200'
                  }`}
                >
                  <div className={`w-4 h-4 rounded shrink-0 border flex items-center justify-center ${
                    isSelected ? 'bg-cyan-500 border-cyan-500 text-zinc-900' : 'border-zinc-600'
                  }`}>
                    {isSelected && <CheckCircle2 className="w-3 h-3" />}
                  </div>
                  <span className="text-sm">{opt.text}</span>
                </button>
              );
            })}
          </div>
          <div className="mt-4 text-xs text-zinc-500 text-center">
            {activePoll.total_votes} vote{activePoll.total_votes > 1 ? 's' : ''} • {activePoll.allow_multiple ? 'Choix multiples possibles' : 'Choix unique'}
          </div>
        </div>
        <div className="p-3 border-t border-zinc-800">
          {view === 'vote' ? (
            <Button onClick={handleVote} disabled={!selectedOptions.length} className="w-full bg-cyan-600 hover:bg-cyan-700 text-white">
              Voter
            </Button>
          ) : isHost ? (
            <Button onClick={handleEnd} variant="destructive" className="w-full">
              Terminer le sondage
            </Button>
          ) : (
            <div className="text-center text-sm text-zinc-400 py-2">Vous avez voté</div>
          )}
        </div>
      </div>
    );
  }

  // List view
  return (
    <div className="flex flex-col h-full bg-zinc-950">
      <div className="p-3 border-b border-zinc-800 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-white">Sondages</h3>
        {isHost && (
          <Button size="sm" onClick={() => setView('create')} className="h-7 text-xs bg-zinc-800 hover:bg-zinc-700 text-white">
            <Plus className="w-3 h-3 mr-1" /> Nouveau
          </Button>
        )}
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {polls.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-40 text-zinc-500">
            <BarChart2 className="w-8 h-8 mb-2 opacity-50" />
            <p className="text-xs">Aucun sondage créé</p>
          </div>
        ) : (
          <AnimatePresence>
            {polls.map(poll => (
              <motion.div key={poll.id} initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }}
                className="p-3 rounded-lg bg-zinc-900 border border-zinc-800 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <h4 className="text-sm font-medium text-white line-clamp-2">{poll.question}</h4>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full whitespace-nowrap ${
                    poll.status === 'active' ? 'bg-cyan-500/20 text-cyan-400' :
                    poll.status === 'closed' ? 'bg-zinc-800 text-zinc-400' : 'bg-amber-500/20 text-amber-400'
                  }`}>
                    {poll.status === 'active' ? 'En cours' : poll.status === 'closed' ? 'Terminé' : 'Brouillon'}
                  </span>
                </div>
                {isHost && poll.status === 'draft' && (
                  <Button size="sm" onClick={() => handleStart(poll.id)} className="w-full h-8 text-xs bg-cyan-600 hover:bg-cyan-700">
                    Lancer ce sondage
                  </Button>
                )}
                {poll.status !== 'draft' && (
                  <div className="text-xs text-zinc-500">
                    {poll.total_votes} votes enregistrés
                  </div>
                )}
              </motion.div>
            ))}
          </AnimatePresence>
        )}
      </div>
    </div>
  );
}
